import React from 'react';
import { Trans } from 'react-i18next';
import styled from 'styled-components';

import { GA4_EVENTS, TRACK_EVENTS } from '@shared/constants/google';
import { trackCustomEvent } from '@shared/google-utils';
import { PRICING_LINK } from '@shared/constants/constants';
import { formatLink } from '@shared/format-helpers';
import { ONE_MONTH_PLAN } from '@shared/constants/checkout';
import { ProductsModal } from '@containers/modals';
import { Modal, Text } from '@components';
// @TODO Look into why importing cards as single Card object breaks CMS functionality
// Review items' cards
import { ReviewCard } from '@components/cards/review-card';
import { MentionCard } from '@components/cards/mention-card';
import { InfluencerCard } from '@components/cards/influencer-card';
import { RatingsCard } from '@containers/common/reviews/components';
import dealsModalImageMobile from '@images/deals/deals-modal-discount.png';
import oneMonth from '@images/plans/1-month.svg';
import threeYear from '@images/plans/3-year.svg';
import oneYear from '@images/plans/1-year.svg';
import AndroidIcon from '@images/devices/android-icon.svg';
import AppleTVIcon from '@images/platform-icons/apple-tv-black.svg';
import iOSIcon from '@images/devices/iOS-icon.svg';
import MacOSIcon from '@images/devices/macOS-icon.svg';
import WindowsIcon from '@images/devices/windows-icon.svg';
import AndroidTVIcon from '@images/devices/android-tv-icon.svg';
import FireTVIcon from '@images/devices/fire-tv-icon.svg';
import LinuxIcon from '@images/devices/linux-icon.svg';
// @TODO Update with new content when available
// Reviews images
import techRadar from '@images/TechRadar.svg';
import bestValueVPN from '@images/best-value-vpn.svg';
import proPrivacy from '@images/pro-privacy.png';
import evanSigel from '@images/influencers/evan-sigel.png';
import nerdExplains from '@images/influencers/nerd-explains.png';
import adamSomething from '@images/influencers/adam-something.png';

export const REVIEW_ITEMS = [
  {
    label: 'App reviews',
    content: [
      {
        customComponent: RatingsCard,
      },
      {
        customComponent: ReviewCard.Apple,
        author: 'Matt_Evans2310',
        rating: 5,
        comment:
          'Atlas VPN is one of the best VPN services I’ve seen on the market, it’s fast efficient and doesn’t give you ads or promos, the premium version is cheap and has amazing features, I’m a software developer and sometimes I need to have that extra layer of security and with Atlas VPN I can feel safe doing it, would 100% recommend',
      },
      {
        customComponent: ReviewCard.Google,
        author: 'Andrew Nyman',
        rating: 5,
        comment:
          "Best bang for your buck. Simple to use and several options. If you don't want to commit to the big boys, this is the VPN for you. 10/10 in price, reliability, functionality, ease of use, and customer service.",
      },
    ],
  },
  {
    label: 'Media mentions',
    cardComponent: MentionCard,
    content: [
      {
        logoImage: {
          src: techRadar,
          alt: 'TechRadar logo',
        },
        comment: 'Capable and powerful VPN platform ticking most industry boxes.',
      },
      {
        logoImage: {
          src: bestValueVPN,
          alt: 'Best Value VPN logo',
        },
        comment: 'Remarkably good considering the low cost of a subscription.',
      },
      {
        logoImage: {
          src: proPrivacy,
          alt: 'ProPrivacy logo',
        },
        comment: 'One of the best VPN services available on the market.',
      },
    ],
  },
  {
    label: 'Influencers',
    cardComponent: InfluencerCard,
    content: [
      {
        image: {
          src: evanSigel,
          alt: 'Evan Sigel logo',
        },
        author: 'Evan Sigel, Jubilee',
        description: '6.6 M subscribers',
        comment:
          'Atlas VPN keeps me connected and secure, it’s super easy to use, and I really love being able to watch my favorite shows abroad with the VPN!',
      },
      {
        image: {
          src: nerdExplains,
          alt: 'Nerd Explains logo',
        },
        author: 'Nerd Explains',
        description: '1 M subscribers',
        comment:
          'If you want to browse freely without being tracked, Atlas VPN is the best option!',
      },
      {
        image: {
          src: adamSomething,
          alt: 'Adam Something logo',
        },
        author: 'Adam Something',
        description: '455 K subscribers',
        comment: 'Atlas VPN has great features, does the job well!',
      },
    ],
  },
];

export const COMPATIBLE_DEVICES = (t) => [
  {
    image: WindowsIcon,
    title: t('common:platforms.windows'),
    link: formatLink(t('navigation:paths.vpn-for-windows')),
    customEvent: { eventName: GA4_EVENTS.devicesCompatibleWindowsClick },
  },
  {
    image: MacOSIcon,
    title: t('common:platforms.macOS'),
    link: formatLink(t('navigation:paths.vpn-for-mac')),
    customEvent: { eventName: GA4_EVENTS.devicesCompatibleMacOsClick },
  },
  {
    image: LinuxIcon,
    title: t('common:platforms.linux'),
    link: formatLink(t('navigation:paths.vpn-for-linux')),
    customEvent: { eventName: GA4_EVENTS.devicesCompatibleLinuxClick },
  },
  {
    image: AndroidIcon,
    title: t('common:platforms.android'),
    link: formatLink(t('navigation:paths.vpn-for-android')),
    customEvent: { eventName: GA4_EVENTS.devicesCompatibleAndroidClick },
  },
  {
    image: iOSIcon,
    title: t('common:platforms.iOS'),
    link: formatLink(t('navigation:paths.vpn-for-iphone')),
    customEvent: { eventName: GA4_EVENTS.devicesCompatibleIOsClick },
  },
  {
    image: AndroidTVIcon,
    title: t('common:platforms.androidTv'),
    link: formatLink(t('navigation:paths.vpn-for-android-tv')),
    customEvent: { eventName: GA4_EVENTS.devicesCompatibleAndroidTvClick },
  },
  {
    image: FireTVIcon,
    title: t('common:platforms.fireTv'),
    link: formatLink(t('navigation:paths.vpn-for-fire-tv')),
    customEvent: { eventName: GA4_EVENTS.devicesCompatibleFireTvClick },
  },
  {
    image: AppleTVIcon,
    title: t('common:platforms.appleTv'),
    link: formatLink(t('navigation:paths.vpn-for-apple-tv')),
    customEvent: { eventName: GA4_EVENTS.devicesCompatibleAppleTvClick },
  },
];

export const getModalConfig = ({ viewEventLabel, closeEventLabel }) => ({
  container: Modal.Container,
  content: ProductsModal,
  onShowCallback: () =>
    trackCustomEvent({
      category: TRACK_EVENTS.category.popUp,
      action: TRACK_EVENTS.action.view,
      label: viewEventLabel,
    }),
  onCloseCallback: () =>
    trackCustomEvent({
      category: TRACK_EVENTS.category.popUp,
      action: TRACK_EVENTS.action.close,
      label: closeEventLabel,
    }),
});

export const getModalProps = ({ pricePerMonth, googleEventLabel }) => ({
  buttonLink: PRICING_LINK,
  buttonLabel: 'Get the Deal',
  buttonProps: {
    customEvent: { eventName: GA4_EVENTS.ctaClick },
  },
  content: {
    label: 'Atlas VPN hot sale!',
    subLabel: (
      <React.Fragment>
        off Atlas VPN Premium —<br />
        it's only {pricePerMonth}/mo!
      </React.Fragment>
    ),
    desktopSubLabel: `off Atlas VPN Premium — it's only $${pricePerMonth}/mo!`,
    description: (
      <React.Fragment>
        Hurry,
        <br />
        Atlas VPN sale
        <br />
        is happening now!
      </React.Fragment>
    ),
    image: dealsModalImageMobile,
    imageDesktop: dealsModalImageMobile,
  },
});

const CrossedSpan = styled.span`
  text-decoration: line-through;
`;

const getDefaultPlanProps = ({ plan, t }) => {
  const {
    currency,
    discountPercentage,
    isHighlighted,
    pricePerMonth,
    price,
    subscriptionPeriod,
    order,
    extraTime,
    regularPrice,
  } = plan;

  return {
    stickerLabel:
      discountPercentage > 0 && t('plans:checkoutCard.saveDiscount', { discountPercentage }),
    title: (
      <Trans
        i18nKey="plans:checkoutCard.title"
        values={{
          title: t(`plans:checkoutCard.periodTitle.${subscriptionPeriod}`),
          extraComment: extraTime && t(`plans:monthsExtra.${extraTime}`),
        }}
        components={[
          <Text.Body2
            as="span"
            $fontWeight="inherit"
            $fontSize="inherit"
            $lineHeight="inherit"
            color="primary"
            whiteSpace="nowrap"
            key={0}
          />,
        ]}
      />
    ),
    pricePerMonth: pricePerMonth,
    pricePeriod: t('plans:checkoutCard.perMonthShort'),
    currency,
    subscriptionPeriod,
    order,
    isHighlighted,
    billingDescription: (
      <Trans
        i18nKey={
          regularPrice
            ? 'plans:checkoutCard.billingDescriptionWithStar'
            : 'plans:checkoutCard.billingDescriptionWithoutRegularPrice'
        }
        values={{
          price,
          regularPrice,
          currency: currency,
          periodicity: regularPrice
            ? t(`plans:checkoutCard.periodicity.${subscriptionPeriod}`)
            : t(`plans:checkoutCard.noDiscountPeriodicity.${subscriptionPeriod}`),
        }}
        components={[<CrossedSpan key={0} />]}
      />
    ),
  };
};

export const remapPlans = (plans, t) =>
  plans.map((plan) => {
    // TODO revisit this `image` implementation, what about when there is no P1Y or P1M and we would have P3M or something else instead?
    // Plans could be sorted by it's order, and then images applied by plan index, but this sorting should be done in the first plan remap function which is done after plans are fetched
    // So oneYear image would be on the first card, threeYear image on the second and oneMonth on the third one. Names of the images should be updated as well
    const imageSrc =
      plan.subscriptionPeriod === 'P1Y'
        ? oneYear
        : plan.subscriptionPeriod === ONE_MONTH_PLAN
        ? oneMonth
        : threeYear;
    return {
      ...getDefaultPlanProps({ plan, t }),
      image: { src: imageSrc, alt: `${plan.title} pricing card` },
      button: {
        link: `${PRICING_LINK}?step=2&selectedPlanPeriod=${plan.subscriptionPeriod}`,
        title: t('common:actions.getPlan', {
          planTitle: t(`plans:checkoutCard.periodTitle.${plan.subscriptionPeriod}`),
        }),
        buttonProps: {
          customEvent: {
            eventName: GA4_EVENTS.periodPlanClick(plan.subscriptionPeriod.toLowerCase()),
          },
        },
      },
    };
  });
